import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ExtensionGroupOption } from 'src/app/pages/extension-register/models/extension-group-option.interface';
import {
   ExtensionChipOptions,
   ExtensionOption,
} from 'src/app/pages/extension-register/models/extension-option.interface';
import { LcsSingleSelectSelectedEvent } from '../../single-select/single-select-base';

import { MatChipInputEvent } from '@angular/material/chips';
import { ExtensionTypes } from 'generated/graphql-types';
import { ExtensionAndExtensionGroupSelectComponent } from '../component/extension-and-group-select.component';
import { ChipInputOption, ChipInputType, IncomingSuperviseeData } from './extension-and-group.model';

@Component({
    providers: [
        { multi: true, provide: NG_VALUE_ACCESSOR, useExisting: PhoneExtensionAndExtensionGroupChipInputComponent },
    ],
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-phone-extension-and-group-chip-input',
    styleUrls: ['./extension-and-group-chip-input.component.scss'],
    templateUrl: './extension-and-group-chip-input.component.html',
    standalone: false
})
export class PhoneExtensionAndExtensionGroupChipInputComponent implements ControlValueAccessor, OnInit {
   @Input() disabled: boolean = false;

   @Input() required: boolean = false;

   @Input() set extensionGroups(extensionGroups: ExtensionGroupOption[]) {
      if (extensionGroups !== undefined) {
         this._extensionGroups = extensionGroups.filter((extensionGroups) => {
            return !this.extensionGroupIdsToExclude.includes(extensionGroups.extensionGroupId);
         });
      }
   }

   @Input() set extensions(extensions: ExtensionChipOptions[]) {
      if (extensions !== undefined) {
         this._extensions = extensions.filter((extension) => {
            if (extension.hasOwnProperty('isArchived')) {
               return extension.type === ExtensionTypes.Phone && extension.isArchived === false;
            } else return extension.type === ExtensionTypes.Phone;
         });
      }
   }

   get extensions(): ExtensionChipOptions[] {
      return this._extensions;
   }

   get extensionGroups(): ExtensionGroupOption[] {
      return this._extensionGroups;
   }

   @Input() form: UntypedFormGroup;

   @Input() extensionIdsToExclude: number[] = [];

   @Input() extensionGroupIdsToExclude: number[] = [];

   @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;

   @ViewChild('groupSelect') groupSelect: ExtensionAndExtensionGroupSelectComponent;

   chipsControl = new UntypedFormControl();

   inputControl = new UntypedFormControl();

   values: ChipInputOption[] = [];

   availableGroups: ExtensionGroupOption[] = [];

   availableExtensions: ExtensionOption[] = [];

   filteredExtensionGroups: ExtensionGroupOption[] = [];

   filteredExtensions: ExtensionOption[] = [];

   private _extensions: ExtensionChipOptions[];

   private _extensionGroups: ExtensionGroupOption[];

   searchTerm: string = '';

   separatorKeysCodes: number[] = [ENTER, COMMA];

   onChange = (values: ChipInputOption[]) => {};

   onTouched: () => void;

   get displayValues(): ChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   ngOnInit() {}

   writeValue(value: IncomingSuperviseeData): void {
      if (value && 'extensionSupervisees' in value && 'extensionGroupSupervisees' in value) {
         const extensions = value.extensionSupervisees.map((item) => ({
            extensionSuperviseeId: item.extensionSuperviseeId,
            extensionGroupSuperviseeId: null,
            extensionGroupId: null,
            extensionId: item.extensionId,
            supervisorExtensionId: item.supervisorExtensionId,
            superviseeExtensionId: item.superviseeExtensionId,
            superviseeExtensionGroupId: null,
            id: item.extensionId || 0,
            type: ChipInputType.Extension,
            value: {
               type: 'Phone',
               name: item.name,
               number: item.number,
            } as ExtensionOption,
            isDeleted: false,
            isArchived: false,
         }));
         const extensionGroups = value.extensionGroupSupervisees.map((item) => ({
            extensionGroupSuperviseeId: item.extensionGroupSuperviseeId,
            extensionSuperviseeId: null,
            queueMemberId: null,
            extensionId: null,
            queueExtensionId: null,
            supervisorExtensionId: null,
            superviseeExtensionId: null,
            extensionGroupId: item.superviseeExtensionGroupId,
            superviseeExtensionGroupId: item.superviseeExtensionGroupId,
            id: item.superviseeExtensionGroupId || 0,
            type: ChipInputType.ExtensionGroup,
            value: {
               name: item.name,
               description: item.description,
            } as ExtensionGroupOption,
            isDeleted: false,
            isArchived: false,
         }));

         this.values = [...extensionGroups, ...extensions];
      } else {
         this.values = [];
      }
      this.chipsControl.setValue(value);

      this.removeErrors();

      this.updateExtensionOptions();
      this.updateExtensionGroupOptions();
   }

   registerOnChange(onChange: (values: ChipInputOption[]) => void) {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   removeValue(value: ChipInputOption): void {
      const index = this.values.findIndex((v) => v.id === value.id && v.type === value.type);
      if (index !== -1) {
         if (this.values[index].id === 0) {
            this.values.splice(index, 1);
         } else {
            this.values[index].isDeleted = true;
         }
         this.updateValueAndControls();
      }
   }

   add(event: MatChipInputEvent): void {
      if (this.groupSelect.isOpen) {
         const value = event.value.trim();
         if (value) {
            const option = this.groupSelect.options.find((o) => o.value?.name?.toLowerCase() === value.toLowerCase());
            if (option && option.value) {
               this.addChip(option.value, ChipInputType.ExtensionGroup);
            }
         }
      }
   }

   selected(event: LcsSingleSelectSelectedEvent<ExtensionGroupOption | ExtensionOption>): void {
      if (event.option.value) {
         const type = 'extensionGroupId' in event.option.value ? ChipInputType.ExtensionGroup : ChipInputType.Extension;
         if (type == ChipInputType.Extension) this.addChip(event.option.value, type);
         if (type == ChipInputType.ExtensionGroup) this.addGroupChip(event.option.value, type);
      }
   }

   onInputChange(event: Event): void {
      this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
      this.filterOptions(this.searchTerm);
   }

   filterOptions(searchTerm: string): void {
      this.filteredExtensionGroups = this.availableGroups.filter(
         (group) =>
            group.name?.toLowerCase().includes(searchTerm) || group.description?.toLowerCase().includes(searchTerm)
      );
      this.filteredExtensions = this.availableExtensions.filter(
         (extension) =>
            (extension.number?.toString().toLowerCase().includes(searchTerm) ||
               extension.name?.toLowerCase().includes(searchTerm)) &&
            extension.type === ExtensionTypes.Phone
      );

      if (this.groupSelect) {
         this.groupSelect.updateFilteredOptions();
      }
   }

   private addChip(value: any, type: ChipInputType): void {
      const existingChipIndex = this.values.findIndex((chip) => chip.id === value.extensionId);

      if (existingChipIndex > -1) {
         this.values[existingChipIndex].isDeleted = false;
         this.updateValueAndControls();
         return;
      }

      const newValue: ChipInputOption = {
         id: value.extensionId,
         type: type,
         extensionGroupSuperviseeId: null,
         supervisorExtensionId: value.supervisorExtensionId,
         extensionGroupId: null,
         superviseeExtensionGroupId: null,
         extensionSuperviseeId: value.extensionSuperviseeId,
         superviseeExtensionId: value.superviseeExtensionId,
         extensionId: value.extensionId,
         value: value,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.updateValueAndControls();
   }
   private addGroupChip(value: any, type: ChipInputType): void {
      const existingChipIndex = this.values.findIndex((chip) => chip.extensionGroupId === value.extensionGroupId);

      if (existingChipIndex > -1) {
         this.values[existingChipIndex].isDeleted = false;
         this.updateValueAndControls();
         return;
      }

      const newValue: ChipInputOption = {
         id: value.extensionGroupId,
         type: type,
         extensionGroupSuperviseeId: value.extensionGroupSuperviseeId,
         supervisorExtensionId: value.supervisorExtensionId,
         extensionGroupId: value.extensionGroupId,
         superviseeExtensionGroupId: value.extensionGroupId,
         extensionSuperviseeId: null,
         superviseeExtensionId: null,
         extensionId: null,
         value: value,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.updateValueAndControls();
   }

   removeErrors(): void {
      if (!this.chipsControl.errors?.required) {
         this.chipsControl.setErrors(null);
      }
   }

   updateExtensionGroupOptions() {
      const selectedGroupIds = new Set(
         this.displayValues.filter((v) => v.type === ChipInputType.ExtensionGroup).map((v) => v.extensionGroupId)
      );

      this.availableGroups = this.extensionGroups.filter(
         (group) =>
            !selectedGroupIds.has(group.extensionGroupId) &&
            !this.extensionGroupIdsToExclude.includes(group.extensionGroupId)
      );

      this.filteredExtensionGroups = this.availableGroups;
   }

   updateExtensionOptions() {
      const selectedExtensionIds = new Set(
         this.displayValues.filter((v) => v.type === ChipInputType.Extension).map((v) => v.extensionId)
      );

      this.availableExtensions = this.extensions.filter(
         (extension) =>
            !selectedExtensionIds.has(extension.extensionId) &&
            !this.extensionIdsToExclude.includes(extension.extensionId) &&
            extension.type === ExtensionTypes.Phone
      );

      this.filteredExtensions = this.availableExtensions;
   }

   updateValueAndControls(): void {
      if (this.groupSelect.panel) {
         this.groupSelect.panel.visible = false;
      }

      this.chipInput.nativeElement.value = '';

      this.inputControl.setValue(null);

      this.chipsControl.setValue(this.displayValues);

      this.onChange(this.values);

      this.removeErrors();

      this.updateExtensionOptions();
      this.updateExtensionGroupOptions();
   }

   getChipDisplay(value: ChipInputOption): string {
      if (value.type === ChipInputType.ExtensionGroup) {
         const group = value.value as ExtensionGroupOption;

         if (group.description && group.description !== '' && group.description !== null) {
            return `${group.name} - ${group.description} (group)`;
         } else {
            return `${group.name} (group)`;
         }
      } else {
         const extension = value.value as ExtensionOption;

         return `${extension.number} - ${extension.name}`;
      }
   }
}
